import { ILockService, SpaceLocks, lockService } from "../services/LockService";
import { useCallback, useEffect, useRef, useState } from "react";

const empty: SpaceLocks = { marks: {}, locks: {}, partial: false };

/**
 * The lock marks of a space's pages (from the manifest) plus `reload`. Failures are silent: a server without locks, or a manifest that
 * cannot be read, simply shows no badges (the tree's own load reports Identity being unavailable). With `autoLoad` false the caller
 * calls `reload` itself (the documentation page does, with every full tree load).
 */
export function useSpaceLocks(spaceId: string | undefined, service: ILockService = lockService(), autoLoad: boolean = true) {
    const [locks, setLocksState] = useState<SpaceLocks>(empty);
    const current = useRef<SpaceLocks>(empty);
    const generation = useRef(0);

    // no re-render when nothing changes (a failed load on a space that shows no locks anyway)
    const setLocks = useCallback((next: SpaceLocks) => {
        if (current.current === next) return;
        current.current = next;
        setLocksState(next);
    }, []);

    const reload = useCallback(() => {
        const mine = ++generation.current;
        if (!spaceId) {
            setLocks(empty);
            return;
        }
        service
            .getManifestLocks(spaceId)
            .then((l) => mine === generation.current && setLocks(l))
            .catch(() => mine === generation.current && setLocks(empty));
    }, [service, spaceId, setLocks]);

    useEffect(() => {
        setLocks(empty);
        if (autoLoad) reload();
    }, [reload, setLocks, autoLoad]);

    return { ...locks, reload };
}
